// src/components/forms/shared/modal/PetStayManager.tsx

import React, { useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { StyledTextField } from '../FormElements';
import RadioButtonGroup from '../RadioButtonGroup';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import EventToast from '../../../common/EventToast';
import { colors } from '../../../../theme/theme';
import {
  SectionContainer,
  SectionDivider,
  InputContainer,
  IncrementButton,
  ManagerFooter,
} from '../../../common/modal/ModalElements';

interface PetStayManagerProps {
  petId: number;
  rateType?: 'Single' | 'Sharing' | 'Custom';
  customRate?: number;
  lineItemDiscount?: number;
  currentDuration: number;
  maxDuration: number;
  bookingLength: number;
  priceSingle?: number;
  priceSharing?: number;
  onSave: (
    duration: number,
    rateType: 'Single' | 'Sharing' | 'Custom',
    customRate?: number,
    lineItemDiscount?: number
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
  hideStayLength?: boolean;
  hideDiscount?: boolean;
  hideRate?: boolean;
}

const RateLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.8rem',
  marginBottom: theme.spacing(0.2),
}));

const RateText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const RatePrice = styled('span')(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
  transition: 'color 0.3s ease',
}));

const CustomRadioLabel = styled('label')<{
  disabled?: boolean;
  checked?: boolean;
}>(({ theme, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '2rem',
  fontWeight: 600,
  color: disabled
    ? theme.palette.text.disabled
    : theme.palette.mode === 'dark'
      ? colors.grey[100]
      : colors.text.dark,
  opacity: disabled ? 0.6 : 1,
  transition: 'color 0.2s ease',
  userSelect: 'none',
  gap: '0.75rem',
  marginTop: theme.spacing(1),

  '&:hover': {
    color: disabled
      ? theme.palette.text.disabled
      : theme.palette.mode === 'dark'
        ? colors.grey[100]
        : colors.text.dark,
  },
}));

const HiddenRadio = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
});

const CustomRadioButton = styled('span')<{
  checked?: boolean;
  disabled?: boolean;
}>(({ theme, checked, disabled }) => ({
  width: '2rem',
  height: '2rem',
  borderRadius: '50%',
  border: `3px solid ${checked ? colors.primary.main : colors.grey[900]}`,
  position: 'relative',
  transition: 'all 0.2s ease',
  backgroundColor: disabled
    ? theme.palette.action.disabledBackground
    : colors.grey[0],
  flexShrink: 0,

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) scale(0)',
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: disabled
      ? theme.palette.text.disabled
      : colors.primary.main,
    transition: 'transform 0.2s ease',
    ...(checked && {
      transform: 'translate(-50%, -50%) scale(1)',
    }),
  },

  'label:hover &': {
    borderColor: colors.primary.main,
  },
}));

const CustomRateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  paddingLeft: '1rem',
}));

const CustomRateTextField = styled(StyledTextField)(({ theme }) => ({
  width: '120px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.6rem',
    fontWeight: 600,
    padding: theme.spacing(0.5),
  },
  '& input:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const DiscountLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.8rem',
  marginBottom: theme.spacing(0.5),
}));

const DiscountText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const DiscountInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const DiscountTextField = styled(StyledTextField)({
  flex: 1,
  minWidth: '140px',
  maxWidth: '140px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: 600,
  },
});

const DiscountPercentNumber = styled('span')<{ value: number }>(
  ({ theme, value }) => ({
    fontSize: '1.9rem',
    fontWeight: 700,
    color:
      value > 0
        ? theme.palette.mode === 'dark'
          ? theme.palette.secondary.light
          : theme.palette.secondary.main
        : theme.palette.text.primary,
    transition: 'color 0.3s ease',
  })
);

const DiscountPercentageSymbol = styled('span')(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const DiscountValueWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 0,
});

const StayLengthLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.8rem',
  marginBottom: theme.spacing(0.5),
}));

const StayLengthText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const DaysCounter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAtMax',
})<{ isAtMax: boolean }>(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.1rem',
}));

const DaysCounterNumber = styled('span')(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
}));

const DaysCounterMax = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isAtMax',
})<{ isAtMax: boolean }>(({ theme, isAtMax }) => ({
  fontSize: '1.9rem',
  fontWeight: 700,
  color: isAtMax
    ? theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main
    : theme.palette.text.primary,
  transition: 'color 0.3s ease',
}));

const DurationTextField = styled(StyledTextField)({
  flex: 1,
  minWidth: '140px',
  maxWidth: '140px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: 600,
  },
});

const PetStayManager: React.FC<PetStayManagerProps> = ({
  petId,
  rateType: initialRateType = 'Single',
  customRate: initialCustomRate,
  lineItemDiscount: initialDiscount = 0,
  currentDuration,
  maxDuration,
  bookingLength,
  priceSingle,
  priceSharing,
  onSave,
  onCancel,
  isLoading = false,
  hideStayLength = false,
  hideDiscount = false,
  hideRate = false,
}) => {
  const [duration, setDuration] = useState<number>(currentDuration);
  const [rateValue, setRateValue] = useState<string>(
    initialRateType.toLowerCase()
  );
  const [customRate, setCustomRate] = useState<string>(
    initialCustomRate?.toString() || ''
  );
  const [lineItemDiscount, setLineItemDiscount] = useState<string>(
    initialDiscount?.toString() || '0'
  );

  const [showToast, setShowToast] = useState(false);
  const [toastPosition, setToastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const currentRate =
    rateValue === 'custom'
      ? parseFloat(customRate) || undefined
      : rateValue === 'sharing'
        ? priceSharing
        : priceSingle;

  const handleRateChange = (value: string) => {
    setRateValue(value);
    if (value !== 'custom') {
      setCustomRate('');
    }
  };

  const handleCustomClick = (event: React.MouseEvent<HTMLLabelElement>) => {
    if (rateValue === 'custom') return;

    const customRadio = event.currentTarget.querySelector(
      'span'
    ) as HTMLElement;
    if (customRadio) {
      const rect = customRadio.getBoundingClientRect();
      setToastPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      setShowToast(true);
    }

    setRateValue('custom');
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (rateValue === 'custom') return;

      const customRadio = e.currentTarget.querySelector('span') as HTMLElement;
      if (customRadio) {
        const rect = customRadio.getBoundingClientRect();
        setToastPosition({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
        setShowToast(true);
      }

      setRateValue('custom');
    }
  };

  const handleCustomRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setCustomRate(value);
    }
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      const numValue = parseFloat(value) || 0;
      if (numValue <= 100) {
        setLineItemDiscount(value);
      }
    }
  };

  const handleDiscountIncrement = () => {
    const currentValue = parseFloat(lineItemDiscount) || 0;
    if (currentValue < 100) {
      const newValue = Math.min(currentValue + 1, 100);
      setLineItemDiscount(newValue.toString());
    }
  };

  const handleDiscountDecrement = () => {
    const currentValue = parseFloat(lineItemDiscount) || 0;
    if (currentValue > 0) {
      const newValue = Math.max(currentValue - 1, 0);
      setLineItemDiscount(newValue.toString());
    }
  };

  const validateDuration = (value: number): number => {
    let validated = value;
    if (validated < 0) validated = 0;
    if (validated > maxDuration) validated = maxDuration;
    return Math.floor(validated);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setDuration(validateDuration(value));
    }
  };

  const handleIncrement = () => {
    setDuration((prev) => validateDuration(prev + 1));
  };

  const handleDecrement = () => {
    setDuration((prev) => validateDuration(prev - 1));
  };

  const isValid = () => {
    if (rateValue === 'custom' && !customRate) return false;
    return true;
  };

  const canDecrement = duration > 0;
  const canIncrement = duration < maxDuration;
  const isAtMax = duration === maxDuration;
  const isCustomChecked = rateValue === 'custom';
  const discountNumber = parseFloat(lineItemDiscount) || 0;
  const canDiscountIncrement = discountNumber < 100;
  const canDiscountDecrement = discountNumber > 0;

  return (
    <Box>
      <SectionContainer>
        {!hideRate && (
          <RateLabel>
            <RateText>Rate:</RateText>
            {currentRate !== undefined && (
              <RatePrice>R{currentRate.toFixed(2)}</RatePrice>
            )}
          </RateLabel>
        )}

        <RadioButtonGroup
          value={rateValue}
          onChange={handleRateChange}
          options={[
            { value: 'single', label: 'Single' },
            { value: 'sharing', label: 'Share' },
          ]}
          direction="horizontal"
          directionForMobile="horizontal"
        />

        <CustomRateContainer>
          <CustomRadioLabel
            htmlFor="custom-rate-radio"
            checked={isCustomChecked}
            onClick={handleCustomClick}
            onKeyDown={handleCustomKeyDown}
            tabIndex={0}
          >
            <HiddenRadio
              type="radio"
              id="custom-rate-radio"
              name="rate"
              value="custom"
              checked={isCustomChecked}
              onChange={() => {}}
              required={false}
            />
            <CustomRadioButton checked={isCustomChecked} aria-hidden="true" />
            Custom
          </CustomRadioLabel>

          <CustomRateTextField
            type="number"
            value={customRate}
            onChange={handleCustomRateChange}
            placeholder="0.00"
            disabled={rateValue !== 'custom'}
            inputProps={{ step: 0.01, min: 0 }}
            size="small"
          />
        </CustomRateContainer>
      </SectionContainer>

      {!hideRate && (!hideDiscount || !hideStayLength) && <SectionDivider />}

      {!hideDiscount && (
        <>
          <SectionContainer>
            <DiscountLabel>
              <DiscountText>Discount:</DiscountText>
              <DiscountValueWrapper>
                <DiscountPercentNumber value={discountNumber}>
                  {discountNumber}
                </DiscountPercentNumber>
                <DiscountPercentageSymbol>%</DiscountPercentageSymbol>
              </DiscountValueWrapper>
            </DiscountLabel>

            <DiscountInputContainer>
              <IncrementButton
                onClick={handleDiscountDecrement}
                disabled={!canDiscountDecrement}
                title="Decrease discount"
              >
                <RemoveIcon />
              </IncrementButton>

              <DiscountTextField
                type="number"
                value={lineItemDiscount}
                onChange={handleDiscountChange}
                placeholder="0"
                inputProps={{ step: 0.01, min: 0, max: 100 }}
                size="small"
              />

              <IncrementButton
                onClick={handleDiscountIncrement}
                disabled={!canDiscountIncrement}
                title="Increase discount"
              >
                <AddIcon />
              </IncrementButton>
            </DiscountInputContainer>
          </SectionContainer>

          {!hideStayLength && <SectionDivider />}
        </>
      )}

      {!hideStayLength && (
        <SectionContainer>
          <StayLengthLabel>
            <StayLengthText>Stay Length:</StayLengthText>
            <DaysCounter isAtMax={isAtMax}>
              <DaysCounterNumber>{duration}</DaysCounterNumber>
              <DaysCounterMax isAtMax={isAtMax}>/{maxDuration}</DaysCounterMax>
            </DaysCounter>
          </StayLengthLabel>
          <InputContainer>
            <IncrementButton
              onClick={handleDecrement}
              disabled={!canDecrement}
              title="Decrease duration"
            >
              <RemoveIcon />
            </IncrementButton>

            <DurationTextField
              type="number"
              value={duration}
              onChange={handleInputChange}
              inputProps={{ min: 0, max: maxDuration, step: 1 }}
              size="small"
            />

            <IncrementButton
              onClick={handleIncrement}
              disabled={!canIncrement}
              title="Increase duration"
            >
              <AddIcon />
            </IncrementButton>
          </InputContainer>
        </SectionContainer>
      )}

      <ManagerFooter>
        <ModalCancelButton onClick={onCancel} disabled={isLoading} />
        <ModalSaveButton
          onClick={() => {
            const capitalizedRateType = (rateValue.charAt(0).toUpperCase() +
              rateValue.slice(1)) as 'Single' | 'Sharing' | 'Custom';
            onSave(
              duration,
              capitalizedRateType,
              rateValue === 'custom' ? parseFloat(customRate) : undefined,
              parseFloat(lineItemDiscount) || 0
            );
          }}
          disabled={isLoading || !isValid()}
        />
      </ManagerFooter>

      <EventToast
        show={showToast}
        position={toastPosition}
        variant="ring"
        onClose={() => setShowToast(false)}
      />
    </Box>
  );
};

export default PetStayManager;
